import { ActivatedRouteSnapshot, Routes, UrlSegment } from '@angular/router';

/** Ajuda, novidades e blog (src/content): baixados só quando uma dessas páginas abre. */
const content = () => import('./content/site-content');

/* O `canMatch` da rota já garante que o artigo existe. */
const helpOf = async (route: ActivatedRouteSnapshot) => (await content()).helpArticle(route.paramMap.get('slug'))!;
const postOf = async (route: ActivatedRouteSnapshot) => (await content()).blogPost(route.paramMap.get('slug'))!;

/**
 * Páginas do site público, dentro da casca do site (menu, rodapé, WhatsApp).
 *
 * `data.page` é o nome da página na mensagem do WhatsApp flutuante;
 * `data.description` vai na descrição da página para buscadores e prévias.
 */
export const siteRoutes: Routes = [
	{
		path: '',
		pathMatch: 'full',
		title: 'ObraSync — diário de obra e portal do cliente para arquitetos',
		data: {
			page: 'inicial',
			description:
				'Registre a visita pelo celular, mantenha o cronograma em dia e mostre a obra ao seu cliente num link. 14 dias grátis, sem cartão.',
		},
		loadComponent: () => import('./home/home.component').then((m) => m.HomeComponent),
	},
	{
		path: 'pricing',
		title: 'Preços do ObraSync — planos para escritórios de arquitetura e interiores',
		data: {
			page: 'de preços',
			footer: 'compact',
			description:
				'Todos os recursos em todos os planos: Autônomo, Estúdio, Escritório e Personalizado. 14 dias grátis com tudo liberado, sem cartão.',
		},
		loadComponent: () => import('./pricing/pricing.component').then((m) => m.PricingComponent),
	},
	{
		path: 'for-architects',
		title: 'ObraSync para arquitetos — diário, cronograma e portal do cliente',
		data: {
			page: 'para arquitetos',
			audience: 'arquitetos',
			description:
				'Várias obras ao mesmo tempo, cada cliente bem informado. Diário pelo celular, cronograma sugerido, custo por obra e um link para o cliente acompanhar.',
		},
		loadComponent: () => import('./audience/audience.component').then((m) => m.AudienceComponent),
	},
	{
		path: 'for-interior-designers',
		title: 'ObraSync para designers de interiores — o cliente acompanha cada entrega',
		data: {
			page: 'para designers de interiores',
			audience: 'interiores',
			description:
				'Fotos da obra em 30 segundos, antes e agora lado a lado e um link para o cliente acompanhar da marcenaria ao acabamento. 14 dias grátis.',
		},
		loadComponent: () => import('./audience/audience.component').then((m) => m.AudienceComponent),
	},
	{
		path: 'contact',
		title: 'Contato · ObraSync',
		data: {
			page: 'de contato',
			footer: 'compact',
			description:
				'Fale com quem faz o ObraSync pelo WhatsApp, veja uma obra de exemplo ou mande seu pedido sobre dados.',
		},
		loadComponent: () => import('./contact/contact.component').then((m) => m.ContactComponent),
	},
	{
		path: 'security',
		title: 'Segurança · ObraSync',
		data: {
			page: 'de segurança',
			description:
				'Entrar sem senha, papéis na equipe, link do cliente fora dos buscadores, cartão no Stripe e seus dados quando quiser.',
		},
		loadComponent: () => import('./legal/security.component').then((m) => m.SecurityComponent),
	},
	{
		path: 'terms',
		title: 'Termos de uso · ObraSync',
		data: { page: 'de termos', description: 'Termos e condições de uso do ObraSync.' },
		loadComponent: () => import('./legal/terms.component').then((m) => m.TermsComponent),
	},
	{
		path: 'privacy',
		title: 'Política de privacidade · ObraSync',
		data: {
			page: 'de privacidade',
			description:
				'Como o ObraSync coleta, guarda e protege os dados do seu escritório e dos seus clientes, conforme a LGPD.',
		},
		loadComponent: () => import('./legal/privacy.component').then((m) => m.PrivacyComponent),
	},
	{
		path: 'cookies',
		title: 'Política de cookies · ObraSync',
		data: {
			page: 'de cookies',
			description: 'O que o ObraSync guarda no seu navegador, para quê e por quanto tempo, e como mudar a sua escolha.',
		},
		loadComponent: () => import('./legal/cookies.component').then((m) => m.CookiesComponent),
	},
	{
		path: 'data-processing',
		title: 'Termo de tratamento de dados · ObraSync',
		data: {
			page: 'do termo de tratamento de dados',
			description:
				'As regras entre o escritório (controlador) e o ObraSync (operador) para os dados das obras e dos clientes, conforme a LGPD.',
		},
		loadComponent: () => import('./legal/data-processing.component').then((m) => m.DataProcessingComponent),
	},
	{
		path: 'help',
		title: 'Central de ajuda · ObraSync',
		data: {
			page: 'de ajuda',
			description:
				'Passo a passo para começar no ObraSync: criar a obra, montar o cronograma, registrar a visita e mandar o link ao cliente.',
		},
		loadComponent: () => import('./help/help.component').then((m) => m.HelpComponent),
	},
	{
		path: 'help/:slug',
		/* Artigo que não existe cai no 404. */
		canMatch: [async (_: unknown, segments: UrlSegment[]) => !!(await content()).helpArticle(segments[1]?.path)],
		title: async (route) => `${(await helpOf(route)).title} · Ajuda do ObraSync`,
		resolve: {
			article: helpOf,
			description: async (route: ActivatedRouteSnapshot) => (await helpOf(route)).description,
		},
		data: { page: 'de ajuda' },
		loadComponent: () => import('./help/help-article.component').then((m) => m.HelpArticleComponent),
	},
	{
		path: 'changelog',
		title: 'Novidades do ObraSync',
		data: {
			page: 'de novidades',
			description: 'O que mudou no ObraSync a cada versão: diário de obra, cronograma, portal do cliente e mais.',
		},
		loadComponent: () => import('./news/news.component').then((m) => m.NewsComponent),
	},
	{
		path: 'blog',
		title: 'Blog do ObraSync — obra e cliente',
		data: {
			page: 'do blog',
			description:
				'Ideias práticas para quem acompanha obra: cronograma, diário, visita técnica e como manter o cliente informado.',
		},
		loadComponent: () => import('./blog/blog.component').then((m) => m.BlogComponent),
	},
	{
		path: 'blog/:slug',
		canMatch: [async (_: unknown, segments: UrlSegment[]) => !!(await content()).blogPost(segments[1]?.path)],
		title: async (route) => `${(await postOf(route)).title} · ObraSync`,
		resolve: {
			post: postOf,
			description: async (route: ActivatedRouteSnapshot) => (await postOf(route)).description,
			image: async (route: ActivatedRouteSnapshot) => (await postOf(route)).image ?? undefined,
		},
		data: { page: 'do blog' },
		loadComponent: () => import('./blog/blog-post.component').then((m) => m.BlogPostComponent),
	},
	{
		path: 'tools',
		title: 'Ferramentas grátis para quem acompanha obra · ObraSync',
		data: {
			page: 'de ferramentas',
			description:
				'Gerador de cronograma de reforma, mensagem da semana para o cliente, modelo de diário de obra (RDO) e checklist de entrega. Grátis, sem cadastro.',
		},
		loadComponent: () => import('./tools/tools.component').then((m) => m.ToolsComponent),
	},
	{
		path: 'tools/renovation-schedule',
		title: 'Gerador de cronograma de reforma grátis · ObraSync',
		data: {
			page: 'do gerador de cronograma',
			description:
				'Escolha o tipo de obra e as datas: as etapas da reforma saem divididas no prazo, prontas para imprimir ou salvar em PDF. Grátis.',
		},
		loadComponent: () => import('./tools/schedule-tool.component').then((m) => m.ScheduleToolComponent),
	},
	{
		path: 'tools/client-message',
		title: 'Mensagem da semana para o cliente da obra · ObraSync',
		data: {
			page: 'da mensagem para o cliente',
			description:
				'Monte em um minuto o resumo da semana da obra para mandar ao cliente pelo WhatsApp: visitas, etapas, progresso e o destaque.',
		},
		loadComponent: () => import('./tools/message-tool.component').then((m) => m.MessageToolComponent),
	},
	{
		path: 'tools/daily-report-template',
		title: 'Modelo de diário de obra (RDO) para imprimir · ObraSync',
		data: {
			page: 'do modelo de RDO',
			description:
				'Folha de diário de obra (RDO) grátis para imprimir ou salvar em PDF: clima, equipe, serviços, materiais, ocorrências e assinaturas.',
		},
		loadComponent: () => import('./tools/rdo-tool.component').then((m) => m.RdoToolComponent),
	},
	{
		path: 'tools/handover-checklist',
		title: 'Checklist de vistoria e entrega de obra · ObraSync',
		data: {
			page: 'do checklist de entrega',
			description:
				'O que conferir antes de entregar a obra ao cliente: pintura, pisos, portas, elétrica, hidráulica, marcenaria e documentos. Grátis.',
		},
		loadComponent: () => import('./tools/checklist-tool.component').then((m) => m.ChecklistToolComponent),
	},
];
